/*
  PINGO — Pause off-screen CSS motion (site-wide default). 2026-07-21.

  Freezes any element running a CONTINUOUS (infinite) CSS @keyframes animation
  while it sits outside the viewport, and resumes it ~200px before it re-enters.
  Decorative CSS loops (sl-divider marquees, and anything added later) otherwise
  keep the compositor ticking forever even when scrolled off-screen — a constant
  GPU/battery drain and a contributor to phones warming up over a session.

  Discovery is automatic: document.getAnimations() → the animations whose timing
  iterates Infinity → their originating element. The paused state is a class
  (pingo-custom.css) that sets `animation-play-state: paused` on the element and
  its ::before / ::after, so pseudo-element loops are covered too.

  NOT responsible for:
    • GSAP ambient loops (pattern drift, Movimento presets) — already self-gate
      via ScrollTrigger, and being rAF/inline-transform (not Web Animations) they
      never appear in getAnimations(). No double-handling.
    • JS rAF loops (e.g. sl-scroll-carousel) — those gate inside their own code.
    • Elements marked [data-pingo-anim-managed] — self-gated CSS loops that must
      opt out. The footer mushroom sprout is a CSS @keyframes loop (GSAP only
      flips a play-state var) so it DOES appear in getAnimations(); but its rest
      frame translates each item 130% out of view, so an IntersectionObserver on
      the item would latch "off-screen" and pause it permanently. It already has
      a correct host-watching gate (initMushroomSprout), so we leave it alone.

  Progressive enhancement: no-op when getAnimations()/IntersectionObserver are
  absent (animations simply run as before — no regression). reduced-motion →
  no-op (there are no running animations to pause).
*/
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window) || typeof document.getAnimations !== 'function') return;

  var PAUSED = 'pingo-anim-off';
  var observed = new WeakSet();

  // Pause when the element leaves the viewport, resume just before it re-enters.
  // The 200px margin means marquees are already moving by the time they're visible
  // (no "starts from frozen" pop on scroll-in).
  var io = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      entries[i].target.classList.toggle(PAUSED, !entries[i].isIntersecting);
    }
  }, { rootMargin: '200px 0px' });

  function scan() {
    var anims = document.getAnimations();
    for (var i = 0; i < anims.length; i++) {
      var eff = anims[i].effect;
      if (!eff || !eff.target) continue;

      var iterations;
      try {
        iterations = eff.getTiming().iterations;
      } catch (e) {
        continue;
      }
      if (iterations !== Infinity) continue; // only continuous loops

      var el = eff.target; // originating element (also for pseudo-element animations)
      // Skip self-gated loops (e.g. footer mushrooms): their rest frame moves the
      // element out of view, so observing it here would latch a permanent pause.
      if (el.nodeType === 1 && el.closest('[data-pingo-anim-managed]')) continue;
      if (observed.has(el)) continue;
      observed.add(el);
      io.observe(el); // IO emits the initial state next frame → off-screen ones pause at once
    }
  }

  function boot() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(scan, { timeout: 1500 });
    } else {
      setTimeout(scan, 400);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor / dynamically-loaded sections may introduce new loops.
  document.addEventListener('shopify:section:load', boot);
}());
